import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import {
  DeleteTeeTimePromotion,
  GetTeeTimePromotions,
  PostTeeTimePromotion,
  PutTeeTimePromotion,
} from "api/rpc/2024-04/facilityAdmin/teesheet/promotion";

import { showError, showSuccess } from "redux/actions/ui";
import { ITeeTimePromotion } from "redux/reducers/models/teetime";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import Input from "components/form/input/Input";
import Sheet from "components/sheet/Sheet";
import Popup from "components/popup/Popup";
import DataTable from "components/dataTable/DataTable";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import { Select } from "components/select/index";
import FormLayout from "components/form/FormLayout";

interface IPromotionFormState {
  title: string;
  promotion_type: "percent" | "amount";
  promotion_value: string;
  promotion_label: string;
}

interface ITeeTimePromotionsState {
  promotions: Array<ITeeTimePromotion> | undefined;
  selectedPromotion: ITeeTimePromotion | null;
  form: IPromotionFormState;
  editModalVisible: boolean;
  newModalVisible: boolean;
  deleteModalVisible: boolean;
}

const emptyForm = (): IPromotionFormState => ({
  title: "",
  promotion_type: "percent",
  promotion_value: "",
  promotion_label: "",
});

function formatPromotionValue(promotion: ITeeTimePromotion): string {
  if (promotion.promotion_type === "percent") {
    return `${promotion.promotion_value}%`;
  }
  return `$${promotion.promotion_value}`;
}

export default function TeeTimePromotions() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [state, setState] = useState<ITeeTimePromotionsState>({
    promotions: undefined,
    selectedPromotion: null,
    form: emptyForm(),
    editModalVisible: false,
    newModalVisible: false,
    deleteModalVisible: false,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadPromotions(source.token);
    return () => {
      source.cancel();
    };
  }, []);

  async function loadPromotions(token?: CancelToken) {
    const promotionRes = await GetTeeTimePromotions(null, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (promotionRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading promotions")));
      return;
    }

    setState(prevState => ({ ...prevState, promotions: promotionRes.data }));
  }

  function validateForm(): boolean {
    if (!state.form.title.trim()) {
      dispatch(showError(t("Please enter a promotion title")));
      return false;
    }
    if (state.form.promotion_value === "" || Number(state.form.promotion_value) < 0) {
      dispatch(showError(t("Please enter a valid promotion discount value")));
      return false;
    }
    if (state.form.promotion_type === "percent" && Number(state.form.promotion_value) > 100) {
      dispatch(showError(t("Promotion percent must be 100 or less")));
      return false;
    }
    return true;
  }

  function formPayload() {
    return {
      title: state.form.title.trim(),
      promotion_type: state.form.promotion_type,
      promotion_value: Number(state.form.promotion_value),
      promotion_label: state.form.promotion_label.trim() === "" ? null : state.form.promotion_label.trim(),
    };
  }

  async function createPromotion() {
    if (!validateForm()) {
      return;
    }

    const promotionRes = await PostTeeTimePromotion(formPayload(), true);

    if (promotionRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating promotion")));
      return;
    }

    dispatch(showSuccess(t("Promotion created successfully")));
    setState(prevState => ({
      ...prevState,
      form: emptyForm(),
      newModalVisible: false,
    }));
    void loadPromotions();
  }

  async function updatePromotion() {
    if (!state.selectedPromotion || !validateForm()) {
      return;
    }

    const promotionRes = await PutTeeTimePromotion(
      {
        id: state.selectedPromotion.id,
        ...formPayload(),
      },
      true,
    );

    if (promotionRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating promotion")));
      return;
    }

    dispatch(showSuccess(t("Promotion updated successfully")));
    setState(prevState => ({
      ...prevState,
      selectedPromotion: null,
      form: emptyForm(),
      editModalVisible: false,
    }));
    void loadPromotions();
  }

  async function deletePromotion() {
    if (!state.selectedPromotion) {
      return;
    }

    const promotionRes = await DeleteTeeTimePromotion({ id: state.selectedPromotion.id }, true);

    if (promotionRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting promotion")));
      return;
    }

    dispatch(showSuccess(t("Promotion deleted successfully")));
    setState(prevState => ({
      ...prevState,
      selectedPromotion: null,
      form: emptyForm(),
      editModalVisible: false,
      deleteModalVisible: false,
    }));
    void loadPromotions();
  }

  function handleEditPromotion(promotion: ITeeTimePromotion) {
    setState(prevState => ({
      ...prevState,
      selectedPromotion: promotion,
      form: {
        title: promotion.title,
        promotion_type: promotion.promotion_type,
        promotion_value: String(promotion.promotion_value),
        promotion_label: promotion.promotion_label ?? "",
      },
      editModalVisible: true,
    }));
  }

  function handleNewPromotion() {
    setState(prevState => ({
      ...prevState,
      form: emptyForm(),
      newModalVisible: true,
    }));
  }

  function handleCancelEdit() {
    setState(prevState => ({
      ...prevState,
      selectedPromotion: null,
      form: emptyForm(),
      editModalVisible: false,
      deleteModalVisible: false,
    }));
  }

  function handleCancelNew() {
    setState(prevState => ({
      ...prevState,
      form: emptyForm(),
      newModalVisible: false,
    }));
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target;
    setState(prevState => ({
      ...prevState,
      form: { ...prevState.form, [id]: value },
    }));
  }

  function renderPromotionForm(formKey: string) {
    return (
      <FormLayout key={formKey}>
        <FormLayout.Group>
          <Input value={state.form.title} label={t("Title")} id="title" onChange={handleInputChange} />
        </FormLayout.Group>
        <FormLayout.Group>
          <Select
            key={`${formKey}-${state.form.promotion_type}`}
            label={t("Type")}
            onChange={(value: "percent" | "amount") =>
              setState(prevState => ({
                ...prevState,
                form: { ...prevState.form, promotion_type: value },
              }))
            }
            defaultValue={state.form.promotion_type}
          >
            <Select.Option value="percent">{t("Percent Off")}</Select.Option>
            <Select.Option value="amount">{t("Amount Off")}</Select.Option>
          </Select>
          <Input
            value={state.form.promotion_value}
            label={state.form.promotion_type === "amount" ? t("Discount Amount") : t("Discount Percent")}
            id="promotion_value"
            type="number"
            onChange={handleInputChange}
            placeholder={state.form.promotion_type === "percent" ? "e.g. 20" : "e.g. 10"}
          />
        </FormLayout.Group>
        <FormLayout.Group>
          <Input
            value={state.form.promotion_label}
            label={t("Promotion Label")}
            id="promotion_label"
            onChange={handleInputChange}
            placeholder={t("Customer-facing label")}
          />
        </FormLayout.Group>
      </FormLayout>
    );
  }

  return (
    <Page
      title={t("Promotions")}
      narrow
      primaryAction={{
        content: t("New Promotion"),
        action: handleNewPromotion,
      }}
    >
      <DataTable
        columns={[
          { label: t("Title"), width: "35%" },
          { label: t("Type"), width: "20%" },
          { label: t("Value"), width: "15%" },
          { label: t("Label"), width: "30%" },
        ]}
        loading={state.promotions === undefined}
      >
        {state.promotions?.length === 0 ? (
          <tr>
            <td colSpan={4}>
              <MessageDisplay
                icon="info-circle"
                title={t("No promotions found")}
                content={t("Create a new promotion to populate this list")}
              />
            </td>
          </tr>
        ) : null}

        {state.promotions
          ? state.promotions.map(promotion => (
              <tr key={promotion.id} onClick={() => handleEditPromotion(promotion)} className="clickable">
                <td>
                  <p className="table-cell-lead-text">{promotion.title}</p>
                </td>
                <td>{promotion.promotion_type === "percent" ? t("Percent Off") : t("Amount Off")}</td>
                <td>{formatPromotionValue(promotion)}</td>
                <td>{promotion.promotion_label || "—"}</td>
              </tr>
            ))
          : null}
      </DataTable>

      <Sheet
        title={t("Edit Promotion")}
        open={state.editModalVisible}
        size="small"
        onCancel={handleCancelEdit}
        onOk={updatePromotion}
        onSecondary={() => setState(prevState => ({ ...prevState, deleteModalVisible: true }))}
        secondaryText={t("Delete")}
        closable
        backDropCancel
        cancelText={t("Cancel")}
        okText={t("Update")}
        overflow
      >
        {renderPromotionForm(`edit-${state.selectedPromotion?.id ?? "none"}`)}
      </Sheet>

      <Sheet
        title={t("New Promotion")}
        open={state.newModalVisible}
        size="small"
        onCancel={handleCancelNew}
        onOk={createPromotion}
        closable
        backDropCancel
        cancelText={t("Cancel")}
        okText={t("Create")}
        overflow
      >
        {renderPromotionForm("new")}
      </Sheet>

      <Popup
        open={state.deleteModalVisible}
        type="warning"
        title={t("Delete Promotion?")}
        description={t(
          "This removes the promotion from the library. Tee times that already have it applied will keep their current discount.",
        )}
        okText={t("Delete")}
        onOk={() => void deletePromotion()}
        onCancel={() => setState(prevState => ({ ...prevState, deleteModalVisible: false }))}
        backDropCancel
      />
    </Page>
  );
}
